import { applyOrderPromotions } from './promotions'

/**
 * Create a retail order with stock-out main fields and payment lines.
 *
 * @param {number} id UI order tab identifier.
 * @param {number|null} customerId Selected customer identifier.
 * @returns {Object} Order state containing UI fields and stock-out main fields.
 */
export const createRetailOrder = (id, customerId = null) => ({
  id,
  search: '',
  activeCategoryId: null,
  scrollTop: 0,
  products: [],
  productsPage: 0,
  productsHasMore: true,
  productsLoading: false,
  stock_out_id: null,
  customer_id: customerId,
  sub_total: 0,
  discount_type: '%',
  discount_rate: 0,
  discount: 0,
  total_after_discount: 0,
  vat_rate: 0,
  vat_amount: 0,
  grand_total: 0,
  is_city_ledger: false,
  fx_rate: null,
  fx_rate_ratio: null,
  location_id: null,
  remarks: '',
  payment_lines: [],
  promotion_selections: {},
  promotion_quantities: {},
  invoice_discount_type: 'percent',
  invoice_discount_rate: 0,
  invoice_discount_amount: 0,
  counter_id: null,
  cartItems: []
})

/**
 * Build the server-owned portion of a retail-sale save request.
 *
 * UI state and calculated totals stay local to the POS screen. The backend
 * recalculates prices, discounts, VAT, promotion quantities, and exchange
 * rates from the compact order contract.
 *
 * @param {Object} order Current retail order state.
 * @param {Object} payment Complete-payment form state.
 * @returns {{order: Object, payment: Object}} Save request payload.
 */
export const buildRetailSalePayload = (order = {}, payment = {}) => ({
  order: {
    customer_id: order.customer_id || null,
    remarks: order.remarks || null,
    promotion_selections: order.promotion_selections || {},
    cartItems: (order.cartItems || []).map(item => ({
      product_id: item.product_id,
      uom_id: item.uom_id,
      uom_name_en: item.uom_name_en || null,
      uom_name_kh: item.uom_name_kh || null,
      name: item.name || null,
      nameKh: item.nameKh || null,
      pro_sale_code_id: item.pro_sale_code_id,
      unit_price: Number(item.unit_price || 0),
      quantity: Number(item.quantity || 0),
      stock_out_quantity: Number(item.stock_out_quantity || item.quantity || 0),
      discount_type: item.discount_type || '%',
      discount_rate: Number(item.discount_rate || 0),
      discount_amount: Number(item.discount_amount || 0),
      total_after_discount: Number(item.total_after_discount || 0),
      vat_rate: Number(item.vat_rate || 0),
      vat_amount: Number(item.vat_amount || 0),
      grand_total: Number(item.grand_total || 0),
      is_free_promotion_item: Boolean(item.is_free_promotion_item),
      promo_type_id: item.promo_type_id || null,
      promo_free_item_cond_id: item.promo_free_item_cond_id || null,
      promotion: item.promotion || null,
      ref_promo_product_id: item.ref_promo_product_id || null,
      remarks: item.remarks || null,
      display_order: item.display_order || null,
      is_allow_adjust_free_quantity: Boolean(item.is_allow_adjust_free_quantity)
    }))
  },
  payment: {
    discount_type: payment.discount_type || 'percent',
    discount_rate: Number(payment.discount_rate || 0),
    is_city_ledger: Boolean(payment.is_city_ledger),
    change_in_usd: Number(payment.change_in_usd || 0),
    change_in_riel: Number(payment.change_in_riel || 0),
    payment_lines: (payment.payment_lines || []).map(line => ({
      payment_method_id: line.payment_method_id || null,
      bank_id: line.bank_id || null,
      bank_account_id: line.bank_account_id || null,
      input_currency_id: line.input_currency_id || null,
      input_amount: Number(line.input_amount || 0)
    }))
  }
})

/**
 * Create a payment line payload. Laravel assigns the payment record ID and
 * resource relationship when the retail sale is saved.
 *
 * @param {Object} values Payment line values.
 * @returns {Object} Payment line ready to be added to a retail order.
 */
export const createPaymentLine = (values = {}) => ({
  payment_method_id: values.payment_method_id || null,
  bank_id: values.bank_id || null,
  bank_account_id: values.bank_account_id || null,
  input_currency_id: values.input_currency_id || null,
  input_amount: Number(values.input_amount) || 0,
  base_currency_id: values.base_currency_id || null,
  ratio_rate_to_base: Number(values.ratio_rate_to_base) || 0,
  to_other_currency: values.to_other_currency || null
})

/**
 * Get the first configured exchange rate for the order header.
 *
 * @param {Array} exchangeRates Configured retail exchange rates.
 * @returns {number|null} Exchange rate or null when none is configured.
 */
export const getDefaultFxRate = exchangeRates => {
  const rate = (exchangeRates || []).find(item =>
    Number.isFinite(Number(item.fx_rate))
  )

  return rate ? Number(rate.fx_rate) : null
}

/**
 * Get the first configured exchange-rate ratio for the order payment lines.
 *
 * @param {Array} exchangeRates Configured retail exchange rates.
 * @returns {number|null} Exchange-rate ratio or null.
 */
export const getDefaultFxRateRatio = exchangeRates => {
  const rate = (exchangeRates || []).find(item =>
    Number.isFinite(Number(item.fx_rate_ratio))
  )

  return rate ? Number(rate.fx_rate_ratio) : null
}

/**
 * Calculate invoice-level totals for a retail order.
 *
 * @param {Object} order Retail order containing cart items and VAT rate.
 * @returns {Object} Invoice totals ready to merge into the order.
 */
export const calculateRetailOrderTotals = (order, promotions = []) => {
  const items = applyOrderPromotions(order, promotions)
  if (order) order.cartItems = items
  const subTotal = items.reduce((total, item) => {
    return total + Number(item.quantity || 0) * Number(item.unit_price || 0)
  }, 0)
  const discount = items.reduce((total, item) => {
    return total + Number(item.discount_amount || 0)
  }, 0)
  const totalAfterDiscount = items.reduce((total, item) => {
    return total + Number(item.total_after_discount || 0)
  }, 0)
  const vatAmount =
    totalAfterDiscount * (Number((order && order.vat_rate) || 0) / 100)

  return {
    sub_total: subTotal,
    discount,
    total_after_discount: totalAfterDiscount,
    vat_amount: vatAmount,
    grand_total: totalAfterDiscount + vatAmount
  }
}
